import React, { useEffect, useState, useRef } from "react";
import styles from "./PartnersPage.module.css";
import {
  getPartnerHome,
  submitPartnerFormData,
} from "../../lib/getPartnerHome";
import PartnersPageSkeleton from "./PartnersPageSkeleton/PartnersPageSkeleton";
import { trackEvent, trackFormSubmission } from "../../hooks/useGTM";
import { metaPixelCatalogService } from "../../services/metaPixelCatalogService";

interface Banner {
  url: string;
  formats: {
    small?: { url: string };
    medium?: { url: string };
    thumbnail?: { url: string };
  };
}

interface MainMessage {
  title: string;
  text: string;
}

interface Reason {
  title: string;
  text: string;
  icon: Banner | null;
}

interface ReasonSection {
  title: string;
  text: string;
  reasons: Reason[];
}

interface Step {
  title: string;
  number: number;
  text: string;
}

interface StepsSection {
  title: string;
  text: string;
  steps: Step[];
}

// Update FormField interface to match schema
interface FormField {
  id?: number;
  label: string;
  slug: string; // Changed from 'name' to 'slug' to match Forms.tsx
  type: string;
  isRequired: boolean; // Changed from 'required' to 'isRequired' to match Forms.tsx
  order?: number;
  placeholder?: string;
  helpText?: string;
  options?: {
    choices?: Array<{ label: string; value: string }>;
  };
  width?: string;
  fullRow?: boolean;
  defaultValue?: string;
}

interface FormSection {
  id?: number;
  title: string;
  order?: number;
  description?: string;
  fields: FormField[];
}

interface Form {
  id: number;
  title: string;
  description: string;
  sections: FormSection[];
  submitButtonLabel: string;
  redirectUrl: string;
  isActive: boolean;
  successTitle: string;
  successText: string;
}

interface PartnerHomeData {
  banner: Banner | null;
  bannerTitle: string;
  bannerSubtitle: string;
  bannerButton: string;
  mainMessage: MainMessage | null;
  reasonSection: ReasonSection | null;
  stepsSection: StepsSection | null;
  form: Form | null;
}

const ReasonIcons = {
  visibility: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  ),
  channel: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  ),
  digital: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  ),
};

const PartnersPage = () => {
  const [data, setData] = useState<PartnerHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string | boolean>>(
    {}
  );
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Create a ref for the form section
  const formSectionRef = useRef<HTMLElement>(null);

  // Function to scroll to form section
  const scrollToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    formSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getPartnerHome();
        setData(result);
      } catch (error) {
        setError(error instanceof Error ? error.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Track page view when component mounts
    trackEvent("partners_page_view", {
      page_title: "Partners Page",
      page_section: "partners",
    });
  }, []);

  // Fix the handleInputChange function to properly handle form changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    // Fix: Use a definite boolean value rather than possibly undefined
    const isCheckbox = type === "checkbox";
    const fieldValue = isCheckbox
      ? !!(e.target as HTMLInputElement).checked
      : value;

    console.log(`Field "${name}" changed to:`, isCheckbox ? fieldValue : value);

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue, // This ensures we never assign undefined
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data?.form) return;

    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      // Use the proper form submission function
      const result = await submitPartnerFormData(data.form.id, formData);

      if (result.success) {
        setFormSubmitted(true);
        console.log("Form submitted successfully, ID:", result.id);

        // Track form submission
        trackFormSubmission(data.form.id.toString(), "Partners Form", {
          form_type: "partner_application",
          form_id: data.form.id,
          submission_id: result.id,
        });

        // Track Meta Pixel form submission
        metaPixelCatalogService.trackFormSubmission(
          "Partners Form",
          data.form.id.toString(),
          true,
          {
            form_type: "partner_application",
            fields_count: Object.keys(formData).length,
            submission_id: result.id,
          }
        );

        // Redirect if URL is provided
        if (data.form?.redirectUrl) {
          setTimeout(() => {
            window.location.href = data.form!.redirectUrl;
          }, 2000);
        }
      } else {
        // Track failed form submission
        metaPixelCatalogService.trackFormSubmission(
          "Partners Form",
          data.form.id.toString(),
          false,
          {
            form_type: "partner_application",
            error: result.error || "Unknown error",
          }
        );

        setSubmissionError(result.error || "Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmissionError(
        error instanceof Error
          ? error.message
          : "An error occurred while submitting the form"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // CSS class helper for field widths
  const getWidthClass = (width?: string): string => {
    switch (width) {
      case "w1/4":
        return styles.width25;
      case "w1/3":
        return styles.width33;
      case "w1/2":
        return styles.width50;
      case "w2/3":
        return styles.width66;
      case "full":
      default:
        return styles.widthFull;
    }
  };

  // Update the renderField function to ensure key uniqueness
  const renderField = (field: FormField, sectionId: number) => {
    // Generate a truly unique ID that won't change between renders
    const fieldId = `field-${sectionId}-${field.slug || field.id}`;
    const widthClass = getWidthClass(field.width || "full");

    // Check if fullRow property exists
    const isFullRow = field.fullRow === true;
    const layoutClass = widthClass;

    return (
      <React.Fragment key={fieldId}>
        <div className={`${styles.formField} ${layoutClass}`}>
          <label htmlFor={fieldId} className={styles.fieldLabel}>
            {field.label}
            {field.isRequired && <span className={styles.requiredMark}>*</span>}
          </label>

          {field.helpText && (
            <div className={styles.helpText}>{field.helpText}</div>
          )}

          {renderFieldInput(field, fieldId)}
        </div>

        {/* Add spacer for full row fields */}
        {isFullRow && (
          <div className={styles.fullRowSpacer} aria-hidden="true"></div>
        )}
      </React.Fragment>
    );
  };

  // Render the appropriate input element based on field type
  const renderFieldInput = (field: FormField, fieldId: string) => {
    switch (field.type) {
      case "text":
      case "email":
      case "tel":
      case "number":
        return (
          <input
            id={fieldId}
            type={field.type}
            name={field.slug}
            placeholder={field.placeholder || ""}
            value={(formData[field.slug] as string) || ""}
            onChange={handleInputChange}
            required={field.isRequired}
            className={styles.formInput}
          />
        );

      case "textarea":
        return (
          <textarea
            id={fieldId}
            name={field.slug}
            placeholder={field.placeholder || ""}
            value={(formData[field.slug] as string) || ""}
            onChange={handleInputChange}
            required={field.isRequired}
            className={styles.formTextarea}
          />
        );

      case "select":
      case "dropdown":
        let options = [];

        // Parse options from different possible formats
        try {
          if (typeof field.options === "string") {
            options = JSON.parse(field.options);
          } else if (Array.isArray(field.options)) {
            options = field.options;
          } else if (
            field.options?.choices &&
            Array.isArray(field.options.choices)
          ) {
            options = field.options.choices;
          }
        } catch (e) {
          console.error("Error parsing options:", e, field.options);
        }

        return (
          <select
            id={fieldId}
            name={field.slug}
            value={(formData[field.slug] as string) || ""}
            onChange={handleInputChange}
            required={field.isRequired}
            className={styles.formSelect}
          >
            <option value="">-- Please select --</option>
            {options.map((option: any, idx: number) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "checkbox":
        return (
          <div className={styles.checkboxContainer}>
            <input
              id={fieldId}
              type="checkbox"
              name={field.slug}
              checked={!!formData[field.slug]}
              onChange={handleInputChange}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxLabel}>
              {field.placeholder || ""}
            </span>
          </div>
        );

      case "radio":
        return (
          <div className={styles.radioGroup}>
            {field.options?.choices?.map((option: any, idx: number) => (
              <div key={idx} className={styles.radioItem}>
                <input
                  id={`${fieldId}-${idx}`}
                  type="radio"
                  name={field.slug}
                  value={option.value}
                  checked={formData[field.slug] === option.value}
                  onChange={handleInputChange}
                  required={field.isRequired}
                  className={styles.radioInput}
                />
                <label
                  htmlFor={`${fieldId}-${idx}`}
                  className={styles.radioLabel}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case "file":
        return (
          <input
            id={fieldId}
            type="file"
            name={field.slug}
            required={field.isRequired}
            className={styles.fileInput}
          />
        );

      default:
        console.log(
          `Unsupported field type: ${field.type} for field ${field.slug}`
        );
        return (
          <input
            id={fieldId}
            type="text"
            name={field.slug}
            required={field.isRequired}
            placeholder={
              field.placeholder || `${field.type} field (not fully supported)`
            }
            value={(formData[field.slug] as string) || ""}
            onChange={handleInputChange}
            className={styles.formInput}
          />
        );
    }
  };

  // Show the skeleton while loading
  if (loading) return <PartnersPageSkeleton />;
  if (error) return <div className={styles.error}>Error: {error}</div>;
  if (!data) return <div className={styles.error}>No data available</div>;

  return (
    <div className={styles.container}>
      {/* Banner Section */}
      <div
        className={styles.banner}
        style={{ backgroundImage: `url(${data.banner?.url})` }}
      >
        <div className={styles.bannerContent}>
          <h1>{data.bannerTitle}</h1>
          <p>{data.bannerSubtitle}</p>
          <button className={styles.bannerButton} onClick={scrollToForm}>
            {data.bannerButton}
          </button>
        </div>
      </div>

      {/* Main Message Section */}
      {data.mainMessage && (
        <section className={styles.mainMessage}>
          <h2>{data.mainMessage.title}</h2>
          <div dangerouslySetInnerHTML={{ __html: data.mainMessage.text }} />
        </section>
      )}

      {/* Reasons Section */}
      {data.reasonSection && (
        <section className={styles.reasonsSection}>
          <h2>{data.reasonSection.title}</h2>
          {data.reasonSection.text && (
            <div
              dangerouslySetInnerHTML={{ __html: data.reasonSection.text }}
            />
          )}
          <div className={styles.reasons}>
            {data.reasonSection.reasons.map((reason: Reason, index: number) => {
              // Choose icon based on index
              let icon = ReasonIcons.visibility;
              if (index === 1) icon = ReasonIcons.channel;
              if (index === 2) icon = ReasonIcons.digital;

              return (
                <div key={index} className={styles.reasonCard}>
                  <div className={styles.reasonIcon}>
                    {reason.icon ? (
                      <img src={reason.icon.url} alt={reason.title} />
                    ) : (
                      icon
                    )}
                  </div>
                  <h3>{reason.title}</h3>
                  <div dangerouslySetInnerHTML={{ __html: reason.text }} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Steps Section */}
      {data.stepsSection && (
        <section className={styles.stepsSection}>
          <h2>{data.stepsSection.title}</h2>
          {data.stepsSection.text && (
            <div dangerouslySetInnerHTML={{ __html: data.stepsSection.text }} />
          )}
          <div className={styles.steps}>
            {data.stepsSection.steps
              .sort((a: Step, b: Step) => a.number - b.number)
              .map((step: Step, index: number) => (
                <div key={index} className={styles.stepCard}>
                  <div className={styles.stepNumber}>{step.number}</div>
                  <h3>{step.title}</h3>
                  <div dangerouslySetInnerHTML={{ __html: step.text }} />
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Form Section - Using schema-aligned implementation */}
      {data.form && data.form.isActive && (
        <section className={styles.formContainer} ref={formSectionRef}>
          {formSubmitted ? (
            <div className={styles.formSuccess}>
              <h2>{data.form?.successTitle || "Thank you!"}</h2>
              <p>
                {data.form?.successText ||
                  "Your form has been submitted successfully."}
              </p>
            </div>
          ) : (
            <>
              <h1 className={styles.formTitle}>{data.form?.title}</h1>
              {data.form?.description && (
                <div className={styles.formDescription}>
                  {data.form?.description}
                </div>
              )}

              {/* Display submission error if there is one */}
              {submissionError && (
                <div className={styles.formError}>{submissionError}</div>
              )}

              <form onSubmit={handleFormSubmit} className={styles.form}>
                {data.form?.sections
                  .sort(
                    (a: FormSection, b: FormSection) =>
                      (a.order || 0) - (b.order || 0)
                  )
                  .map((section: FormSection) => (
                    <div
                      key={section.id || Math.random()}
                      className={styles.formSection}
                    >
                      {section.title && (
                        <h2 className={styles.sectionTitle}>{section.title}</h2>
                      )}

                      {section.description && (
                        <div
                          className={styles.sectionDescription}
                          dangerouslySetInnerHTML={{
                            __html: section.description || "",
                          }}
                        />
                      )}

                      <div className={styles.fieldsGrid}>
                        {section.fields
                          .sort(
                            (a: FormField, b: FormField) =>
                              (a.order || 0) - (b.order || 0)
                          )
                          .map((field: FormField) =>
                            renderField(field, section.id || 0)
                          )}
                      </div>
                    </div>
                  ))}

                <div className={styles.formActions}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={styles.formSubmitButton}
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : data.form?.submitButtonLabel || "Submit"}
                  </button>
                </div>
              </form>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default PartnersPage;
